import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Check, Heart, Eye, Send, Infinity as InfinityIcon, Loader2 } from "lucide-react";

const FEATURES = {
  quietplus_monthly: [
    { icon: InfinityIcon, text: "Unlimited likes — no daily cap" },
    { icon: Eye, text: "See your admirers before matching" },
    { icon: Send, text: "Unlimited thoughtful notes" },
    { icon: Heart, text: "Cancel anytime, no questions" },
  ],
  notes_pack_5: [
    { icon: Send, text: "5 thoughtful notes — 180 chars each" },
    { icon: Heart, text: "Attached to your like, read before matching" },
    { icon: Sparkles, text: "Pair with Claude icebreakers for max charm" },
    { icon: Check, text: "Never expire" },
  ],
};

export default function BillingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/billing/packages").then(({ data }) => setPackages(data));
    api.get("/billing").then(({ data }) => setBilling(data)).catch(() => {});
  }, []);

  const startCheckout = async (packageId) => {
    setBusy(packageId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout");
      setBusy(null);
    }
  };

  const qplus = billing?.quietplus;
  const expires = billing?.subscription_expires_at;

  return (
    <div className="min-h-screen grain-bg" data-testid="billing-page">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <button data-testid="billing-back" onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#737373] hover:text-[#1A1818]">
          <ArrowLeft size={16} /> Back
        </button>
        <Link to="/" className="font-serif italic text-2xl text-[#913831]">rendezvous</Link>
        <div className="w-16" />
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 pb-24">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs uppercase tracking-[0.3em] text-[#913831]">For {user?.name}</div>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter text-[#1A1818] mt-3 leading-tight">
            Earn more <em className="text-[#913831]">yeses</em>.
          </h1>
          <p className="mt-4 text-[#737373] max-w-xl">
            Two thoughtful upgrades. Both delete the noise, neither charges you to exist here.
          </p>
        </motion.div>

        {qplus && (
          <div className="mt-8 inline-flex items-center gap-2 bg-[#F3E8DD] border border-[#D4A373]/40 rounded-full px-5 py-2.5 text-sm" data-testid="qplus-active">
            <Sparkles size={14} className="text-[#913831]" /> Quiet+ active until {new Date(expires).toLocaleDateString()}
          </div>
        )}

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {packages.map((p) => (
            <PackageCard
              key={p.id}
              pkg={p}
              isActive={qplus && p.id === "quietplus_monthly"}
              features={FEATURES[p.id] || []}
              onBuy={() => startCheckout(p.id)}
              loading={busy === p.id}
              billing={billing}
            />
          ))}
        </div>

        <p className="mt-10 text-xs text-[#737373] text-center max-w-md mx-auto">
          Stripe test mode. Use card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC.
          You can switch to live keys in production.
        </p>
      </main>
    </div>
  );
}

function PackageCard({ pkg, isActive, features, onBuy, loading, billing }) {
  const headline = pkg.id === "quietplus_monthly" ? "Quiet+" : "Thoughtful Notes";
  const sub = pkg.id === "quietplus_monthly" ? "30 days" : "5-pack";
  const isSub = pkg.id === "quietplus_monthly";

  return (
    <motion.div
      data-testid={`package-${pkg.id}`}
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className={`rounded-3xl p-7 border ${
        isSub ? "bg-[#1A1818] text-white border-[#1A1818]" : "bg-white text-[#1A1818] border-[#E5DFD3]"
      } relative overflow-hidden`}
    >
      {isSub && (
        <div className="absolute top-5 right-5 text-xs uppercase tracking-[0.25em] text-[#D4A373]">most loved</div>
      )}
      <div className={`text-xs uppercase tracking-[0.2em] ${isSub ? "text-white/60" : "text-[#737373]"}`}>{sub}</div>
      <h3 className={`font-serif text-4xl mt-2 ${isSub ? "" : "text-[#1A1818]"}`}>{headline}</h3>
      <p className={`mt-2 text-sm ${isSub ? "text-white/70" : "text-[#737373]"}`}>{pkg.description}</p>

      <div className="mt-6 flex items-baseline gap-1">
        <span className={`font-serif text-5xl ${isSub ? "text-white" : "text-[#913831]"}`}>${pkg.amount}</span>
        <span className={`text-sm ${isSub ? "text-white/60" : "text-[#737373]"}`}>{isSub ? "/ 30 days" : " one-time"}</span>
      </div>

      <ul className="mt-7 space-y-3">
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <li key={i} className={`flex items-start gap-3 text-sm ${isSub ? "text-white/90" : "text-[#1A1818]"}`}>
              <Icon size={16} strokeWidth={1.5} className={isSub ? "text-[#D4A373] mt-0.5" : "text-[#913831] mt-0.5"} />
              <span>{f.text}</span>
            </li>
          );
        })}
      </ul>

      <button
        data-testid={`buy-${pkg.id}`}
        onClick={onBuy}
        disabled={loading || isActive}
        className={`mt-8 w-full rounded-full py-3.5 font-medium transition-transform hover:scale-[1.01] active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 ${
          isSub
            ? "bg-[#D4A373] hover:bg-[#C7945F] text-[#1A1818]"
            : "bg-[#913831] hover:bg-[#7A2E28] text-white"
        }`}
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        {isActive ? "Already active" : (isSub ? "Begin Quiet+" : "Buy 5-pack")}
      </button>

      {!isSub && billing && (
        <div className="mt-3 text-xs text-center text-[#737373]" data-testid="current-notes">
          You have <span className="font-serif text-lg italic text-[#913831]">{billing.note_credits}</span> note{billing.note_credits === 1 ? "" : "s"}
        </div>
      )}
    </motion.div>
  );
}
